'use client'

import { useMemo } from 'react'
import { usePeopleData, type PersonWithLastInteraction } from '@/entries'
import { useTopics } from '@/topics'
import {
  useEntryAnalysis,
  type AnyAnalysis,
} from '../analysis/useEntryAnalysis'
import { useMediaEnrichment } from '@/enrichment'
import type { MediaType as EnrichmentMediaType } from '@/enrichment'
import type { EnrichedMediaResult } from '@/enrichment'
import { useLabelChecking } from '../validation/useLabelChecking'
import type { CaptureEntryType } from '../types'
import type { LabelSuggestion } from '../media-preview/types'

// ── Type-reason heuristics ──────────────────────────────────────────────
function deriveTypeReason(
  aiDetectedType: string | null,
  analysis: AnyAnalysis | null,
): string | null {
  if (!aiDetectedType || !analysis) return null
  const a = analysis as unknown as Record<string, unknown>

  if (aiDetectedType === 'task') {
    if (a.dueDate) return 'mentions a deadline'
    if (Array.isArray(a.checklistItems) && a.checklistItems.length > 0)
      return 'action items detected'
    return 'imperative language'
  }

  if (aiDetectedType === 'journal') {
    return 'personal reflection'
  }

  // Media types — no reason needed (media badge is not shown via this path)
  return null
}

const ENRICHABLE_TYPES: EnrichmentMediaType[] = [
  'movie',
  'book',
  'artist',
  'label',
  'release',
]

function isEnrichableType(type: string | null): type is EnrichmentMediaType {
  return type !== null && ENRICHABLE_TYPES.includes(type as EnrichmentMediaType)
}

interface UseFormAnalysisOptions {
  content: string
  checklistItemsLength: number
  userSelectedType: CaptureEntryType | null
  isSubmitting: boolean
  availableProjects: Array<{ id: string; name: string }>
}

export interface FormAnalysisReturn {
  // People data
  availablePeople: PersonWithLastInteraction[]
  isPeopleLoading: boolean
  peopleForAnalysis: Array<{ id: string; name: string }>

  // Topics data
  allTopics: Array<{ id: string; name: string; entryType?: string | null }>
  availableTopics: Array<{
    id: string
    name: string
    entryType?: string | null
  }>

  // Analysis
  analysis: AnyAnalysis | null
  isAnalyzing: boolean
  aiDetectedType: string | null
  isMediaDetected: boolean
  resetAnalysis: () => void

  // Effective type
  effectiveType: CaptureEntryType | null

  // Enrichment
  enrichedData: EnrichedMediaResult | null
  isEnriching: boolean
  enrichmentError: string | null
  resetEnrichment: () => void
  retryEnrichment: () => void
  setEnrichedData: (data: EnrichedMediaResult) => void

  // Projects
  availableProjects: Array<{ id: string; name: string }>

  // Type reason (heuristic explanation for AI-detected type)
  typeReason: string | null

  // Label checking
  labelSuggestions: LabelSuggestion[]
  isCheckingLabels: boolean
  toggleLabelSuggestion: (id: string) => void
}

/**
 * Combines data fetching, AI analysis, media enrichment, and label checking
 * into a single hook for the unified form orchestrator.
 */
export function useFormAnalysis({
  content,
  checklistItemsLength,
  userSelectedType,
  isSubmitting,
  availableProjects,
}: UseFormAnalysisOptions): FormAnalysisReturn {
  // Data fetching
  const { data: availablePeople = [], isLoading: isPeopleLoading } =
    usePeopleData()
  const { topics: allTopics = [] } = useTopics()

  // Memoized people list for analysis
  const peopleIdsKey = useMemo(
    () => availablePeople.map((p) => p.id).join(','),
    [availablePeople],
  )
  const peopleForAnalysis = useMemo(
    () => availablePeople.map((p) => ({ id: p.id, name: p.title })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [peopleIdsKey],
  )

  // AI analysis
  const {
    analysis,
    isAnalyzing,
    detectedType: aiDetectedType,
    isMedia: isMediaDetected,
    reset: resetAnalysis,
  } = useEntryAnalysis<AnyAnalysis>({
    entryType: 'auto',
    content,
    availablePeople: peopleForAnalysis,
    availableTopics: allTopics.map((t) => t.name),
    allTopicsWithTypes: allTopics.map((t) => ({
      name: t.name,
      entryType: t.entryType,
    })),
    availableProjects: availableProjects.map((p) => p.name),
    enabled: !isSubmitting && !checklistItemsLength,
  })

  // Effective type
  const effectiveType: CaptureEntryType | null = isMediaDetected
    ? null
    : (userSelectedType ?? (aiDetectedType as CaptureEntryType | null))

  // Filter topics by effectiveType
  const availableTopics = useMemo(() => {
    if (!effectiveType) return allTopics
    return allTopics.filter(
      (topic) => !topic.entryType || topic.entryType === effectiveType,
    )
  }, [allTopics, effectiveType])

  // Media enrichment
  const {
    enrichedData,
    isEnriching,
    error: enrichmentError,
    reset: resetEnrichment,
    retry: retryEnrichment,
    setEnrichedData,
  } = useMediaEnrichment({
    type:
      isMediaDetected && isEnrichableType(aiDetectedType)
        ? aiDetectedType
        : null,
    analysis:
      isMediaDetected && isEnrichableType(aiDetectedType) ? analysis : null,
    enabled:
      isMediaDetected && isEnrichableType(aiDetectedType) && !isSubmitting,
  })

  // Type reason heuristic
  const typeReason = useMemo(
    () => deriveTypeReason(aiDetectedType, analysis),
    [aiDetectedType, analysis],
  )

  // Label checking for artists
  const { labelSuggestions, isCheckingLabels, toggleLabelSuggestion } =
    useLabelChecking({ enrichedData })

  return {
    availablePeople,
    isPeopleLoading,
    peopleForAnalysis,
    allTopics,
    availableTopics,
    availableProjects,
    analysis,
    isAnalyzing,
    aiDetectedType,
    isMediaDetected,
    resetAnalysis,
    effectiveType,
    typeReason,
    enrichedData,
    isEnriching,
    enrichmentError,
    resetEnrichment,
    retryEnrichment,
    setEnrichedData,
    labelSuggestions,
    isCheckingLabels,
    toggleLabelSuggestion,
  }
}
